import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
  Signal,
  TemplateRef
} from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeEnum } from '../../services/theme/models/enums/theme.enum';
import { ThemeService } from '../../services/theme/theme.service';
import { SortIconComponent } from '../sort-icon/sort-icon.component';
import { Sorts } from '../../services/pagination/models/typies/sorts.type';
import { Subscription } from "rxjs";

@Component({
    selector: 'l-list-container',
    templateUrl: './list-container.component.html',
    imports: [
        IconComponent,
        NgTemplateOutlet
    ]
})
export class ListContainerComponent<T> implements OnDestroy, AfterViewInit {

  @HostBinding('class')
  public classes = 'block border-radius foreground p-5 overflow-hidden';

  @ContentChild('header', { static: true })
  public header!: TemplateRef<any>;

  @ContentChild('body', { static: true })
  public body!: TemplateRef<any>;

  @Input({ required: true })
  public set total(value: number) {
    this._total = value;
    this.updatePageStatus();
  };

  @Input({ required: true })
  public set size(value: number) {
    this._size = value;
    this.updatePageStatus();
  }

  @Input({ required: true })
  public set page(value: number) {
    this._page = value;
    this.updatePageStatus();
  }

  @Input()
  public sort?: Sorts;

  @Input({ required: true })
  public data: T[] = [];

  @Input({ required: true })
  public isLoading: boolean = false;

  @Output()
  public pageChange: EventEmitter<number> = new EventEmitter();

  @Output()
  public sortChange: EventEmitter<Sorts> = new EventEmitter();

  public get total(): number {
    return this._total;
  }

  public get size(): number {
    return this._size;
  }

  public get page(): number {
    return this._page;
  }

  public isFirstPage: boolean = false;

  public isLastPage: boolean = false;

  public skeletons = Array.from({ length: 10 });

  private readonly orderSubscriptions: Subscription[] = [];

  private _total: number = 0;

  private _size: number = 0;

  private _page: number = 0;

  private readonly themeService = inject(ThemeService);

  private readonly sortableIcons: SortIconComponent[] = [];
  
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public theme: Signal<ThemeEnum> = this.themeService.theme;

  public ngAfterViewInit(): void {
    this.updatePageStatus();
  }

  public ngOnDestroy(): void {
    this.orderSubscriptions.forEach(s => s.unsubscribe());
  }

  public nextPage(): void {
    this.updatePageStatus();

    if (this.isLastPage)
      return;

    this._page = this.page + 1;
    this.pageChange.emit(this.page);
  }

  public prevPage(): void {
    this.updatePageStatus();

    if (this.isFirstPage)
      return;

    this._page = this.page - 1;
    this.pageChange.emit(this.page);
  }

  public registerSortableColumn(sortIcon: SortIconComponent): void {
    this.sortableIcons.push(sortIcon);
    this.checkSort();

    const subscription = sortIcon.changeOrder.subscribe((value) => {
      this.onOrder(sortIcon.field, value);
    });

    this.orderSubscriptions.push(subscription);
  }

  private updatePageStatus(): void {
    const lastPage = Math.ceil(this.total / this.size);

    this.isLastPage = this.page === lastPage;
    this.isFirstPage = this.page === 1;
  }

  private checkSort(): void {
    for (const icon of this.sortableIcons) {
      if (icon.field === this.sort?.field)
        icon.simpleOrder = this.sort?.order === 'ASC' ? 1 : -1;
      else
        icon.simpleOrder = 0;
    }
  }

  private onOrder(field: string, order: number): void {
    const sort = this.sortableIcons
      .find(s => s.field !== field && s.simpleOrder !== 0);

    if (sort)
      sort.simpleOrder = 0;

    if (order === 0) {
      this.sortChange.emit(undefined);
      return;
    }

    this.sortChange.emit({ field, order: order === 1 ? 'ASC' : 'DESC' });
  }

}
